import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { detectMobilePlatform } from '../config/appStores';
import './AppDownloadNudge.css';

const BANNER_SESSION_KEY = 'tabtrips_app_download_banner_dismissed';
const HIDDEN_PATHS = ['/download-app', '/get-app'];

function isBannerDismissedThisSession() {
  try {
    return sessionStorage.getItem(BANNER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function dismissBannerForSession() {
  try {
    sessionStorage.setItem(BANNER_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
}

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return mobile;
}

/**
 * Mobile-only: promotional download banner (session-dismissible) + persistent FAB.
 */
const AppDownloadNudge = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);
  const [bannerOpen, setBannerOpen] = useState(() => !isBannerDismissedThisSession());
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    setPlatform(detectMobilePlatform());
  }, []);

  const pathHidden = HIDDEN_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  const closeBanner = useCallback((e) => {
    e?.stopPropagation?.();
    dismissBannerForSession();
    setBannerOpen(false);
  }, []);

  const openDownload = useCallback(() => {
    navigate('/download-app');
  }, [navigate]);

  if (!isMobile || pathHidden) return null;

  const fabIconClass =
    platform === 'ios'
      ? 'fab fa-apple'
      : platform === 'android'
        ? 'fab fa-android'
        : 'fas fa-download';

  return (
    <>
      <AnimatePresence>
        {bannerOpen && (
          <motion.aside
            className="adn-banner"
            role="dialog"
            aria-label="Download TabTrips Extranet app"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          >
            <button
              type="button"
              className="adn-banner-close"
              aria-label="Close download banner"
              onClick={closeBanner}
            >
              <i className="fas fa-times" aria-hidden />
            </button>

            <button type="button" className="adn-banner-body" onClick={openDownload}>
              <span className="adn-banner-icon" aria-hidden>
                <i className={fabIconClass} />
              </span>
              <span className="adn-banner-copy">
                <span className="adn-banner-title">Get the Extranet app</span>
                <span className="adn-banner-sub">
                  Manage rates &amp; bookings on the go
                </span>
              </span>
              <span className="adn-banner-cta">
                Download
                <i className="fas fa-arrow-right" aria-hidden />
              </span>
            </button>
          </motion.aside>
        )}
      </AnimatePresence>

      {!bannerOpen && (
        <motion.button
          type="button"
          className="adn-fab"
          aria-label="Download TabTrips Extranet app"
          onClick={openDownload}
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 420, damping: 22, delay: 0.05 }}
          whileTap={{ scale: 0.94 }}
        >
          <span className="adn-fab-pulse" aria-hidden />
          <i className={fabIconClass} aria-hidden />
          <span className="adn-fab-label">App</span>
        </motion.button>
      )}
    </>
  );
};

export default AppDownloadNudge;
